import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, ArrowLeft,
  Database, KeyRound, Zap, RefreshCcw, ClockAlert, Wand2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";

const STATUS_STYLES = {
  ok:       { bg: "bg-emerald-50",  border: "border-emerald-200",  text: "text-emerald-800", Icon: CheckCircle2, label: "OK" },
  warning:  { bg: "bg-amber-50",    border: "border-amber-200",    text: "text-amber-800",   Icon: AlertTriangle, label: "Warning" },
  critical: { bg: "bg-rose-50",     border: "border-rose-200",     text: "text-rose-800",    Icon: XCircle,       label: "Critical" },
  disabled: { bg: "bg-gray-50",     border: "border-gray-200",     text: "text-gray-600",    Icon: XCircle,       label: "Deshabilitado" },
};

export default function AdminSecopHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(7);
  const [refreshingSchemas, setRefreshingSchemas] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [correctionTarget, setCorrectionTarget] = useState(null); // {dataset, suggestions, error, loading}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/secop/health?window_days=${windowDays}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar Dataset Health");
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { load(); }, [load, reloadTick]);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  const refreshAllSchemas = useCallback(async () => {
    setRefreshingSchemas(true);
    try {
      const { data } = await api.post("/admin/secop/datasets/refresh-all-schemas");
      const errs = (data.results || []).filter((r) => r.error).length;
      if (errs > 0) {
        toast.warning(`${data.refreshed} schema(s) refrescados · ${errs} con error`);
      } else {
        toast.success(`${data.refreshed} schema(s) refrescados`);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al refrescar schemas");
    } finally {
      setRefreshingSchemas(false);
    }
  }, [refresh]);

  const openSuggestCorrection = useCallback(async (dataset) => {
    setCorrectionTarget({ dataset, loading: true, suggestions: [], error: null });
    try {
      const { data } = await api.post(`/admin/secop/datasets/${dataset.id}/suggest-correction`);
      setCorrectionTarget({
        dataset, loading: false,
        suggestions: data.suggestions || [],
        error: data.error,
        query: data.query,
        currentDatasetId: data.current_dataset_id,
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al buscar sugerencias");
      setCorrectionTarget(null);
    }
  }, []);

  const applyCorrection = useCallback(async (newDatasetId) => {
    if (!correctionTarget?.dataset) return;
    try {
      await api.post(`/admin/secop/datasets/${correctionTarget.dataset.id}/apply-correction`, {
        new_dataset_id: newDatasetId,
      });
      toast.success(`Dataset corregido: ${correctionTarget.dataset.label} → ${newDatasetId}`);
      setCorrectionTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al aplicar corrección");
    }
  }, [correctionTarget, refresh]);

  const fixAllBroken = useCallback(async () => {
    const broken = (data?.datasets || []).filter(
      (d) => d.status === "critical" && d.last_test_ok === false,
    );
    if (broken.length === 0) {
      toast.info("No hay datasets críticos con last_test_ok=false para corregir");
      return;
    }
    toast.info(`Buscando sugerencias para ${broken.length} dataset(s) roto(s)…`);
    // Procesamos secuencial para no martillar el catalog
    for (const d of broken) {
      try {
        const { data: sug } = await api.post(`/admin/secop/datasets/${d.id}/suggest-correction`);
        if (sug.suggestions && sug.suggestions.length > 0) {
          // Abre el modal con el primer dataset; el admin elige uno y luego se reabre para el siguiente
          setCorrectionTarget({
            dataset: d, loading: false,
            suggestions: sug.suggestions,
            error: sug.error,
            query: sug.query,
            currentDatasetId: sug.current_dataset_id,
          });
          break;  // Procesa uno a la vez interactivamente
        }
      } catch (_) { /* skip */ }
    }
  }, [data]);

  const globalStyle = data ? STATUS_STYLES[data.global_status] : STATUS_STYLES.ok;
  const summary = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="admin-secop-health-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/admin/secop/contracts" className="text-gray-500 hover:text-primary text-xs flex items-center gap-1">
              <ArrowLeft size={12} /> Volver a contratos
            </Link>
          </div>
          <h1 className="text-2xl font-serif" data-testid="secop-health-title">Dataset Health</h1>
          <p className="text-xs text-gray-600">
            Cruza la salud de datasets (test, latencia, schema) y tokens en una ventana móvil.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-[11px]">
            {[7, 30].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setWindowDays(d)}
                className={`px-2 py-1 border rounded-sm ${
                  windowDays === d
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
                data-testid={`secop-health-window-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={fixAllBroken}
            className="rounded-sm h-9 text-xs"
            data-testid="secop-health-fix-broken-btn"
            title="Sugerir dataset_ids correctos vía catálogo Socrata"
          >
            <Wand2 size={13} className="mr-1.5 text-amber-600" />
            Corregir IDs curados
          </Button>
          <Button
            variant="outline"
            onClick={refreshAllSchemas}
            disabled={refreshingSchemas}
            className="rounded-sm h-9 text-xs"
            data-testid="secop-health-refresh-schemas-btn"
          >
            <RefreshCcw size={13} className={`mr-1.5 ${refreshingSchemas ? "animate-spin" : ""}`} />
            Refrescar schemas
          </Button>
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading}
            className="rounded-sm h-9 px-2"
            data-testid="secop-health-refresh-btn"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </header>

      {/* Score global */}
      {data && (
        <div
          className={`${globalStyle.bg} ${globalStyle.border} border-2 px-4 py-4 rounded-sm flex items-center justify-between flex-wrap gap-3`}
          data-testid="secop-health-global-card"
        >
          <div className="flex items-center gap-3">
            <globalStyle.Icon size={26} className={globalStyle.text} />
            <div>
              <div className={`text-xs uppercase tracking-widest ${globalStyle.text}`}>Salud global</div>
              <div className={`font-serif text-xl ${globalStyle.text}`} data-testid="secop-health-global-status">
                {globalStyle.label}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <Stat label="Datasets críticos" value={summary.datasets_critical} testid="secop-health-stat-ds-critical" warn={summary.datasets_critical > 0} />
            <Stat label="Datasets warning" value={summary.datasets_warning} testid="secop-health-stat-ds-warning" warn={summary.datasets_warning > 0} />
            <Stat label="Schemas stale" value={summary.schema_stale_count} testid="secop-health-stat-schema-stale" warn={summary.schema_stale_count > 0} />
            <Stat label="Tokens críticos" value={summary.tokens_critical} testid="secop-health-stat-tokens-critical" warn={summary.tokens_critical > 0} />
          </div>
        </div>
      )}

      {/* Datasets */}
      <section className="border border-gray-200 bg-white" data-testid="secop-health-datasets-section">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Database size={14} className="text-emerald-600" />
          <h2 className="font-serif text-base">Datasets ({data?.datasets?.length ?? 0})</h2>
          <span className="text-[10px] uppercase tracking-widest text-gray-500">
            schema stale &gt; {summary.schema_freshness_days ?? 30}d
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-left">Dataset</th>
                <th className="px-3 py-2 text-right">Latencia mediana</th>
                <th className="px-3 py-2 text-right">p95</th>
                <th className="px-3 py-2 text-right">Muestras</th>
                <th className="px-3 py-2 text-left">Último test</th>
                <th className="px-3 py-2 text-left">Schema</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data?.datasets?.map((d, i) => {
                const sty = STATUS_STYLES[d.status] || STATUS_STYLES.ok;
                return (
                  <tr key={d.id} className="border-t border-gray-100" data-testid={`secop-health-ds-row-${i}`}>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm text-[10px] ${sty.bg} ${sty.border} ${sty.text}`}>
                        <sty.Icon size={10} /> {sty.label}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-gray-800">{d.label}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{d.source} · {d.dataset_id}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-700">
                      {d.median_latency_ms != null ? `${Math.round(d.median_latency_ms)}ms` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-700">
                      {d.p95_latency_ms != null ? `${Math.round(d.p95_latency_ms)}ms` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-500">{d.sample_size}</td>
                    <td className="px-3 py-2 text-[11px]">
                      {d.last_tested_at ? (
                        <>
                          {d.last_test_ok ? (
                            <CheckCircle2 size={11} className="inline text-emerald-600 mr-1" />
                          ) : (
                            <XCircle size={11} className="inline text-rose-600 mr-1" />
                          )}
                          <span className="text-gray-600">
                            hace {d.last_test_age_days}d
                          </span>
                          {d.last_test_error && (
                            <div className="text-[10px] text-rose-600 truncate max-w-[160px]" title={d.last_test_error}>
                              {String(d.last_test_error).slice(0, 50)}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 italic">nunca</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px]">
                      {d.schema_refreshed_at ? (
                        <span className={d.schema_stale ? "text-amber-700" : "text-gray-600"}>
                          {d.schema_stale && <ClockAlert size={11} className="inline mr-0.5" />}
                          hace {d.schema_age_days}d
                        </span>
                      ) : (
                        <span className="text-amber-700 italic flex items-center gap-1">
                          <ClockAlert size={11} /> nunca refrescado
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {(d.status === "critical" || d.last_test_ok === false) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openSuggestCorrection(d)}
                          className="h-7 px-2 text-[11px] text-amber-700 hover:bg-amber-50"
                          title="Sugerir dataset_id correcto desde el catálogo Socrata"
                          data-testid={`secop-health-suggest-fix-${i}`}
                        >
                          <Wand2 size={11} className="mr-1" />
                          Sugerir
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Tokens */}
      <section className="border border-gray-200 bg-white" data-testid="secop-health-tokens-section">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <KeyRound size={14} className="text-indigo-600" />
          <h2 className="font-serif text-base">Tokens ({data?.tokens?.length ?? 0})</h2>
          <span className="text-[10px] uppercase tracking-widest text-gray-500">últimos {windowDays}d</span>
        </div>
        {!data?.tokens || data.tokens.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500" data-testid="secop-health-tokens-empty">
            No hay tokens en el pool.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2 text-left">Etiqueta</th>
                  <th className="px-3 py-2 text-right">Requests ({windowDays}d)</th>
                  <th className="px-3 py-2 text-right">Errores ({windowDays}d)</th>
                  <th className="px-3 py-2 text-right">Error rate</th>
                </tr>
              </thead>
              <tbody>
                {data.tokens.map((t, i) => {
                  const sty = STATUS_STYLES[t.status] || STATUS_STYLES.ok;
                  return (
                    <tr key={t.id} className="border-t border-gray-100" data-testid={`secop-health-token-row-${i}`}>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm text-[10px] ${sty.bg} ${sty.border} ${sty.text}`}>
                          <sty.Icon size={10} /> {sty.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-800">{t.label || <span className="italic text-gray-400">sin nombre</span>}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-700">{t.request_count_window}</td>
                      <td className={`px-3 py-2 text-right font-mono tabular-nums ${t.error_count_window > 0 ? "text-rose-600" : "text-gray-500"}`}>
                        {t.error_count_window}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono tabular-nums ${t.error_rate_window >= 0.2 ? "text-rose-600 font-medium" : "text-gray-600"}`}>
                        {(t.error_rate_window * 100).toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {loading && !data && (
        <div className="p-8 text-center text-sm text-gray-500">Cargando salud…</div>
      )}

      {/* Modal de sugerencias */}
      {correctionTarget && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="secop-correction-modal"
        >
          <div className="bg-white border border-gray-300 rounded-sm max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 size={14} className="text-amber-600" />
                <h3 className="font-serif text-base">Corregir dataset_id</h3>
              </div>
              <button
                type="button"
                onClick={() => setCorrectionTarget(null)}
                className="text-gray-500 hover:text-gray-800 text-xs"
                data-testid="secop-correction-close"
              >
                Cerrar
              </button>
            </div>
            <div className="px-4 py-3 text-xs space-y-2 border-b border-gray-100">
              <div>
                <span className="text-gray-500">Dataset:</span>{" "}
                <strong>{correctionTarget.dataset.label}</strong>
              </div>
              <div>
                <span className="text-gray-500">ID actual (no responde):</span>{" "}
                <code className="bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded-sm">
                  {correctionTarget.currentDatasetId}
                </code>
              </div>
              {correctionTarget.query && (
                <div>
                  <span className="text-gray-500">Búsqueda en catálogo:</span>{" "}
                  <em>&ldquo;{correctionTarget.query}&rdquo;</em>
                </div>
              )}
            </div>
            <div className="p-4">
              {correctionTarget.loading ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                  Consultando catálogo Socrata…
                </div>
              ) : correctionTarget.error ? (
                <div className="text-rose-700 text-xs">Error: {correctionTarget.error}</div>
              ) : correctionTarget.suggestions.length === 0 ? (
                <div className="text-center text-gray-500 text-xs py-4">
                  Sin sugerencias del catálogo. Busca el dataset_id manualmente en{" "}
                  <a
                    href={`https://www.datos.gov.co/browse?q=${encodeURIComponent(correctionTarget.query || "")}`}
                    target="_blank" rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    datos.gov.co/browse ↗
                  </a>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="secop-correction-suggestions">
                  {correctionTarget.suggestions.map((s, i) => (
                    <li
                      key={s.dataset_id}
                      className="border border-gray-200 hover:border-emerald-300 rounded-sm p-3 flex items-start gap-3 text-xs"
                      data-testid={`secop-correction-suggestion-${i}`}
                    >
                      <code className="bg-gray-100 px-1.5 py-0.5 font-mono shrink-0">{s.dataset_id}</code>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800">{s.name}</div>
                        {s.description && (
                          <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">
                            {s.description}
                          </div>
                        )}
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {s.page_views ? `${s.page_views.toLocaleString("es-CO")} views · ` : ""}
                          <a href={s.permalink} target="_blank" rel="noreferrer" className="hover:underline">
                            ver en datos.gov.co
                          </a>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={() => applyCorrection(s.dataset_id)}
                        className="rounded-sm h-8 text-[11px] shrink-0"
                        data-testid={`secop-correction-apply-${i}`}
                      >
                        Aplicar
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, warn = false }) {
  return (
    <div className="bg-white/70 border border-gray-200 px-2 py-1.5 rounded-sm" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-mono tabular-nums text-base ${warn ? "text-rose-600 font-medium" : "text-gray-800"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}
